/* eslint-disable jsx-a11y/label-has-associated-control */
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ReadSigningConfig } from '@/services/api/generated/templatesV2Service';
import {
  useTemplateSigningConfigCreateMutation,
  useTemplateSigningConfigUpdateMutation,
} from '@/services/api/templatesV2ServiceEnhanced';

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const signingConfigSchema = z.object({
  provider: z.literal('ADOBE_SIGN'),
  client_signature_required: z.boolean().default(false),
  attorney_signature_required: z.boolean().default(false),
  default_attorney_id: z.string().optional().nullable(),
});

type SigningConfigFormData = z.infer<typeof signingConfigSchema>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SigningConfigEditorProps {
  open: boolean;
  onClose: () => void;
  templateId: string;
  signingConfig?: ReadSigningConfig | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SigningConfigEditor = ({
  open,
  onClose,
  templateId,
  signingConfig,
}: SigningConfigEditorProps) => {
  const isEdit = Boolean(signingConfig);

  const [createConfig, { isLoading: isCreating }] =
    useTemplateSigningConfigCreateMutation();
  const [updateConfig, { isLoading: isUpdating }] =
    useTemplateSigningConfigUpdateMutation();
  const isSaving = isCreating || isUpdating;

  const {
    register,
    handleSubmit,
    reset,
  } = useForm<SigningConfigFormData>({
    resolver: zodResolver(signingConfigSchema),
    defaultValues: {
      provider: 'ADOBE_SIGN',
      client_signature_required: signingConfig?.client_signature_required ?? false,
      attorney_signature_required: signingConfig?.attorney_signature_required ?? false,
      default_attorney_id: signingConfig?.default_attorney_id ?? null,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        provider: 'ADOBE_SIGN',
        client_signature_required: signingConfig?.client_signature_required ?? false,
        attorney_signature_required: signingConfig?.attorney_signature_required ?? false,
        default_attorney_id: signingConfig?.default_attorney_id ?? null,
      });
    }
  }, [open, signingConfig, reset]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const onSubmit = useCallback(
    async (data: SigningConfigFormData) => {
      const body = {
        provider: data.provider,
        client_signature_required: data.client_signature_required,
        attorney_signature_required: data.attorney_signature_required,
        default_attorney_id: data.default_attorney_id || null,
      };

      try {
        if (isEdit) {
          await updateConfig({ templateId, body }).unwrap();
          toast.success('Signing configuration updated');
        } else {
          await createConfig({ templateId, body }).unwrap();
          toast.success('Signing configuration created');
        }
        handleClose();
      } catch {
        toast.error(`Failed to ${isEdit ? 'update' : 'create'} signing configuration`);
      }
    },
    [isEdit, templateId, createConfig, updateConfig, handleClose],
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit Signing Config' : 'Add Signing Config'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the signing configuration for this template.'
              : 'Configure e-signature requirements for this template.'}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4 px-1"
          id="signing-config-form"
        >
          {/* Provider (read-only for now) */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Provider
            </label>
            <input
              type="text"
              value="Adobe Sign"
              disabled
              className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm font-medium text-gray-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-500"
            />
            <input type="hidden" {...register('provider')} />
          </div>

          {/* Client signature */}
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              {...register('client_signature_required')}
              className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
            />
            Require client signature
          </label>

          {/* Attorney signature */}
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              {...register('attorney_signature_required')}
              className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
            />
            Require attorney signature
          </label>

          {/* Default attorney ID */}
          <div className="space-y-1.5">
            <label
              htmlFor="default_attorney_id"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Default Attorney ID
            </label>
            <input
              id="default_attorney_id"
              type="text"
              {...register('default_attorney_id')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-medium shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
              placeholder="Optional attorney UUID"
            />
          </div>
        </form>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button outline type="button" onClick={handleClose}>
            Cancel
          </Button>
          <div className="flex-1" />
          <Button
            color="blue"
            type="submit"
            form="signing-config-form"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Config'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
